import { useTranslation } from 'react-i18next';

import { ApiError, NETWORK_ERROR_CODE, UNEXPECTED_ERROR_CODE } from '../api/ApiError';

/**
 * Zamienia błąd z API na zdanie dla człowieka.
 *
 * Backend oddaje `code` (klucz i18n) + `args`, a konwencja klucza jest **ta sama
 * po obu stronach** — `<moduł>.<obszar>.<element>` (spec 4C). Dzięki temu
 * `graph.relation.second_parent` z serwera trafia wprost w klucz tłumaczenia,
 * bez żadnej mapy pośredniej, którą trzeba by utrzymywać.
 *
 * Gdy klucza nie ma w tłumaczeniach — a to zdarzy się przy każdym nowym błędzie
 * na backendzie — pokazujemy komunikat ogólny zamiast surowego kodu. Kod trafia
 * do konsoli, żeby dało się go znaleźć, ale użytkownik nigdy nie ogląda
 * `graph.element.not_found`.
 */
export function useApiErrorMessage(): (error: unknown) => string {
  const { t, i18n } = useTranslation();

  return (error: unknown): string => {
    if (!(error instanceof ApiError)) {
      return t(UNEXPECTED_ERROR_CODE);
    }

    if (error.code === NETWORK_ERROR_CODE) return t(NETWORK_ERROR_CODE);

    // ⚠ `args` idą też do `exists`, nie tylko do `t`. Bez nich klucz z formami
    // liczebnikowymi (`…_one` / `…_few` / `…_many`) wygląda na nieistniejący — i18next
    // rozwiązuje mnogość dopiero, gdy zna `count`. Objaw jest mylący: komunikat brzmi
    // „Coś poszło nie tak", a tłumaczenie leży na miejscu.
    // Pusty obiekt zamiast `undefined`: przy `exactOptionalPropertyTypes` sygnatura `t`
    // nie przyjmuje opcji, które mogą być `undefined`.
    const args = (error.args ?? {}) as Record<string, unknown>;

    if (i18n.exists(error.code, args)) {
      return t(error.code, args);
    }

    console.warn(`[i18n] missing translation for error code: ${error.code}`);

    if (error.isUnauthorized) return t('common.error.unauthorized');
    if (error.isForbidden) return t('common.error.forbidden');

    return t(UNEXPECTED_ERROR_CODE);
  };
}
